"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LocalDateInterval = void 0;
/* eslint-disable functional/no-classes */
const TimeDuration_1 = require("./TimeDuration");
const LocalDate_1 = require("./LocalDate");
const YearQuarter_1 = require("./YearQuarter");
/**
 * Represents an interval between two LocalDate instances.
 *
 * The interval is inclusive of the start date and exclusive of the end date.
 */
class LocalDateInterval {
    start;
    end;
    constructor(start, end) {
        this.start = start;
        this.end = end;
    }
    /**
     * Returns true if the given date is within the interval.
     *
     * @param date Date to check
     * @returns True if the given date is within the interval
     */
    contains(date) {
        return (LocalDate_1.LocalDate.isEqual(date, this.start) ||
            (LocalDate_1.LocalDate.isBefore(this.start, date) &&
                LocalDate_1.LocalDate.isBefore(date, this.end)));
    }
    /**
     * Returns true if the interval overlaps with the given interval.
     *
     * @param interval Interval to check
     * @returns True if the interval overlaps with the given interval
     */
    overlaps(interval) {
        return (LocalDate_1.LocalDate.isBefore(this.start, interval.end) &&
            LocalDate_1.LocalDate.isBefore(interval.start, this.end));
    }
    /**
     * Returns the intersection of the interval with the given interval.
     *
     * @param interval Interval to intersect with
     * @returns The intersection of the interval with the given interval
     */
    intersection(interval) {
        if (!this.overlaps(interval)) {
            return null;
        }
        return new LocalDateInterval(LocalDate_1.LocalDate.isBefore(this.start, interval.start)
            ? interval.start
            : this.start, LocalDate_1.LocalDate.isBefore(this.end, interval.end) ? this.end : interval.end);
    }
    /**
     * Returns the union of the interval with the given interval.
     *
     * @param interval Interval to union with
     * @returns The union of the interval with the given interval
     */
    union(interval) {
        return new LocalDateInterval(LocalDate_1.LocalDate.isBefore(this.start, interval.start)
            ? this.start
            : interval.start, LocalDate_1.LocalDate.isBefore(this.end, interval.end) ? interval.end : this.end);
    }
    /**
     * Returns the Duration of the interval.
     */
    toDuration() {
        return new TimeDuration_1.TimeDuration(this.end.toDateUTC().getTime() - this.start.toDateUTC().getTime());
    }
    /**
     * Returns an array of all the quarters within the interval of dates.
     */
    toQuarters() {
        const quarters = [];
        // eslint-disable-next-line functional/no-let
        let currentQuarter = YearQuarter_1.YearQuarter.fromLocalDate(this.start);
        // eslint-disable-next-line functional/no-loop-statements
        while (currentQuarter.interval().end.compare(this.end) <= 0) {
            // eslint-disable-next-line functional/immutable-data, functional/no-expression-statements
            quarters.push(currentQuarter);
            const nextYear = currentQuarter.quarter >= 4
                ? currentQuarter.year + 1
                : currentQuarter.year;
            const nextQuarter = currentQuarter.quarter >= 4 ? 1 : currentQuarter.quarter + 1;
            // eslint-disable-next-line functional/no-expression-statements
            currentQuarter = new YearQuarter_1.YearQuarter(nextYear, nextQuarter);
        }
        return quarters;
    }
    /**
     * If the start date is after the end date, returns a new interval with the
     * start and end dates swapped.
     */
    abs() {
        return this.start.compare(this.end) <= 0
            ? this
            : new LocalDateInterval(this.end, this.start);
    }
}
exports.LocalDateInterval = LocalDateInterval;
